// ==============================================================================
// GERADOR DE PAYLOAD PIX (PADRÃO BR CODE / BANCO CENTRAL DO BRASIL - EMV)
// Gera string oficial do Copia e Cola com cálculo de CRC-16 (CCITT-FALSE)
// ==============================================================================

function formatEMV(id: string, value: string): string {
  const size = value.length.toString().padStart(2, '0');
  return `${id}${size}${value}`;
}

export function calculateCRC16(payload: string): string {
  let crc = 0xFFFF;
  const polynomial = 0x1021;

  for (let i = 0; i < payload.length; i++) {
    crc ^= (payload.charCodeAt(i) << 8);
    for (let j = 0; j < 8; j++) {
      if ((crc & 0x8000) !== 0) {
        crc = ((crc << 1) ^ polynomial) & 0xFFFF;
      } else {
        crc = (crc << 1) & 0xFFFF;
      }
    }
  }

  return crc.toString(16).toUpperCase().padStart(4, '0');
}

export interface PixPayloadParams {
  pixKey: string;
  pixKeyType?: 'cpf' | 'cnpj' | 'phone' | 'email' | 'random' | string;
  merchantName: string;
  merchantCity?: string;
  amount?: number;
  txId?: string;
  description?: string;
}

/**
 * Sanitiza a chave PIX conforme especificações oficiais do Banco Central do Brasil (BR Code / EMV)
 * - CPF: 11 dígitos numéricos limpos
 * - CNPJ: 14 dígitos numéricos limpos
 * - Telefone: formato internacional E.164 (+55DDDNÚMERO)
 * - Email: lowercase e trim
 * - EVP (Aleatória): lowercase e trim (formato UUID)
 */
export function sanitizePixKey(key: string, keyType?: string): string {
  if (!key) return '';
  const trimmed = key.trim();

  // Se for email
  if (keyType === 'email' || trimmed.includes('@')) {
    return trimmed.toLowerCase();
  }

  // Se for UUID / EVP
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed);
  if (keyType === 'random' || isUuid) {
    return trimmed.toLowerCase();
  }

  const digitsOnly = trimmed.replace(/\D/g, '');

  // Forçado explicitamente como telefone
  if (keyType === 'phone' || keyType === 'telefone') {
    if (digitsOnly.startsWith('55') && digitsOnly.length >= 12) {
      return `+${digitsOnly}`;
    }
    return `+55${digitsOnly}`;
  }

  // Se o usuário digitou com prefixo internacional +55
  if (trimmed.startsWith('+')) {
    const cleanDigits = trimmed.slice(1).replace(/\D/g, '');
    return `+${cleanDigits}`;
  }

  // CNPJ (14 dígitos)
  if (keyType === 'cnpj' || digitsOnly.length === 14) {
    return digitsOnly;
  }

  // Se continha parênteses de DDD ou traço de celular (ex: (11) 99999-9999) sem ponto
  if (trimmed.includes('(') || (trimmed.includes('-') && !trimmed.includes('.'))) {
    if (digitsOnly.length === 10 || digitsOnly.length === 11) {
      return `+55${digitsOnly}`;
    }
  }

  // CPF (11 dígitos formatados com pontos ex: 000.000.000-00)
  if (keyType === 'cpf' || (digitsOnly.length === 11 && trimmed.includes('.'))) {
    return digitsOnly;
  }

  // Caso genérico de 11 dígitos
  if (digitsOnly.length === 11) {
    return digitsOnly;
  }

  return digitsOnly.length > 0 ? digitsOnly : trimmed;
}

export function generatePixPayload({
  pixKey,
  pixKeyType,
  merchantName,
  merchantCity = 'SAO PAULO',
  amount,
  txId = '***',
  description
}: PixPayloadParams): string {
  if (!pixKey || !merchantName) return '';

  // Sanitização oficial BACEN
  const cleanKey = sanitizePixKey(pixKey, pixKeyType);
  const cleanName = merchantName.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().substring(0, 25);
  const cleanCity = merchantCity.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().substring(0, 15);
  const cleanTxId = txId.replace(/[^a-zA-Z0-9]/g, '').substring(0, 25) || '***';

  // 00 - Payload Format Indicator
  let payload = formatEMV('00', '01');

  // 26 - Merchant Account Information (GUI + Chave + Descrição)
  let merchantAccount = formatEMV('00', 'br.gov.bcb.pix');
  merchantAccount += formatEMV('01', cleanKey);
  if (description) {
    const cleanDesc = description.normalize('NFD').replace(/[\u0300-\u036f]/g, '').substring(0, 40);
    merchantAccount += formatEMV('02', cleanDesc);
  }
  payload += formatEMV('26', merchantAccount);

  // 52 - Merchant Category Code
  payload += formatEMV('52', '0000');

  // 53 - Transaction Currency (986 = BRL)
  payload += formatEMV('53', '986');

  // 54 - Transaction Amount
  if (amount && amount > 0) {
    payload += formatEMV('54', amount.toFixed(2));
  }

  // 58 - Country Code
  payload += formatEMV('58', 'BR');

  // 59 - Merchant Name
  payload += formatEMV('59', cleanName);

  // 60 - Merchant City
  payload += formatEMV('60', cleanCity);

  // 62 - Additional Data Field Template (txId)
  const additionalData = formatEMV('05', cleanTxId);
  payload += formatEMV('62', additionalData);

  // 63 - CRC16
  payload += '6304';
  const checksum = calculateCRC16(payload);

  return `${payload}${checksum}`;
}

export const generateDynamicPixPayload = generatePixPayload;

